import { isTutorialMode } from '@/tutorial/mode';
import { persistStorage } from '@/tutorial/storage';

/**
 * "Lembrar de mim" — ONDE a sessão do CRM fica guardada. Ponto único: tokens
 * (`services/api.ts`) e o `authStore` persistido leem daqui.
 *
 * - marcado   → `localStorage` (sobrevive a fechar o navegador; refresh de 30 d);
 * - desmarcado → `sessionStorage` (morre com a aba; refresh de 1 d).
 *
 * A escolha é um marcador DENTRO do `sessionStorage`. Sem marcador vale o
 * `localStorage` — e é isso que mantém válida a sessão de quem já estava logado
 * antes desta mudança (migração sem passo nenhum: nada a mover).
 *
 * No modo demonstração (tutorial) tudo cai no armazenamento em memória do
 * iframe, como o resto dos stores persistidos.
 */

export const AUTH_TOKEN_KEY = 'opruscrm_token';
export const AUTH_REFRESH_TOKEN_KEY = 'opruscrm_refresh_token';
export const AUTH_STORE_KEY = 'opruscrm-auth';
/** Chaves legadas que o logout sempre limpou junto com os tokens. */
const LEGACY_AUTH_KEYS = ['opruscrm_user', 'oprus-active-company'] as const;
const SESSION_ONLY_MARKER_KEY = 'opruscrm_session_only';

const AUTH_KEYS = [AUTH_TOKEN_KEY, AUTH_REFRESH_TOKEN_KEY, AUTH_STORE_KEY] as const;

function removeKeys(storage: Storage, keys: readonly string[]): void {
  for (const key of keys) storage.removeItem(key);
}

function isSessionOnly(): boolean {
  return window.sessionStorage.getItem(SESSION_ONLY_MARKER_KEY) === '1';
}

/** O `Storage` onde a sessão atual vive. Resolvido a cada chamada. */
export function authStorage(): Storage {
  if (isTutorialMode()) return persistStorage();
  return isSessionOnly() ? window.sessionStorage : window.localStorage;
}

/**
 * Aplica a escolha do login que ACABOU de dar certo — chamar antes de gravar
 * os tokens novos. Apaga a sessão do outro armazenamento para não sobrar um
 * token de 30 dias esquecido no `localStorage` de quem pediu sessão curta (ou
 * o inverso).
 */
export function setAuthPersistence(rememberMe: boolean): void {
  if (isTutorialMode()) return;
  if (rememberMe) {
    window.sessionStorage.removeItem(SESSION_ONLY_MARKER_KEY);
    removeKeys(window.sessionStorage, AUTH_KEYS);
    return;
  }
  removeKeys(window.localStorage, AUTH_KEYS);
  window.sessionStorage.setItem(SESSION_ONLY_MARKER_KEY, '1');
}

/** Logout: limpa a sessão dos DOIS armazenamentos e esquece a escolha. */
export function clearAuthPersistence(): void {
  if (isTutorialMode()) return;
  for (const storage of [window.localStorage, window.sessionStorage]) {
    removeKeys(storage, [...AUTH_KEYS, ...LEGACY_AUTH_KEYS]);
  }
  window.sessionStorage.removeItem(SESSION_ONLY_MARKER_KEY);
}

/**
 * `Storage` para o `persist` do zustand. O `createJSONStorage` resolve o
 * storage UMA vez, na criação do store — por isso este objeto delega a
 * `authStorage()` em cada operação, e a troca de escolha no login vale para a
 * próxima gravação do `authStore` sem recriar nada.
 */
export const authPersistStorage: Storage = {
  get length() {
    return authStorage().length;
  },
  clear: () => authStorage().clear(),
  getItem: (key) => authStorage().getItem(key),
  key: (index) => authStorage().key(index),
  removeItem: (key) => authStorage().removeItem(key),
  setItem: (key, value) => authStorage().setItem(key, value),
};
